import ChapterBadge from "./ChapterBadge";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  ArrowsClockwise, 
  Target, 
  Lightning, 
  FileText 
} from "@phosphor-icons/react";

const WhatOnTenacityDelivers = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const cards = [
    {
      icon: ArrowsClockwise,
      title: "What changed",
      description: "AI surfaces the most important changes across the business.",
    },
    {
      icon: Target,
      title: "What matters now",
      description: "AI prioritizes the signals leadership actually needs to act on.",
    },
    {
      icon: Lightning,
      title: "What needs action",
      description: "AI turns fragmented updates into clear action points and follow-up questions.",
    },
    {
      icon: FileText,
      title: "What the sources say",
      description: "Every brief and answer is grounded in strong enterprise context and trusted source material.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section 
      id="what-tenacity-does"
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative"
      data-testid="what-tenacity-delivers-section"
    >
      {/* Background */}
      <div className="absolute inset-0 grid-pattern opacity-50" />
      
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <ChapterBadge number="04" name="Capabilities" />
          <span className="mono-label" data-testid="section-label">
            AI-Powered Outcomes
          </span>
          <h2 
            className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit']"
            data-testid="section-title"
          >
            What OnTenacity AI <span className="gradient-text">delivers</span>
          </h2>
          <p className="mt-4 text-[#A1A1AA] max-w-2xl mx-auto text-base md:text-lg">
            OnTenacity uses AI to synthesize enterprise signals into decision-ready outputs for leadership.
          </p>
        </motion.div>

        {/* Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          data-testid="delivers-cards-grid"
        >
          {cards.map((card) => (
            <motion.div
              key={card.title}
              variants={cardVariants}
              className="bg-[#0F0F10] p-6 md:p-8 rounded-xl border border-white/5 card-hover"
              data-testid={`delivers-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center mb-4">
                <card.icon size={24} weight="duotone" className="text-[#00E5FF]" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2 font-['Outfit']">
                {card.title}
              </h3>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">
                {card.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WhatOnTenacityDelivers;
